import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import type { Candidate, Job } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { computeRoleFitPair } from './computePairFit';
import { rolefitProjectionService } from '@core/supabase/services/rolefitProjectionService';
import type { RoleFitProjection } from '@core/types';
import { RoleFitPairDetailView, type RoleFitPairTabId } from './RoleFitPairDetailView';

const VALID_TABS: RoleFitPairTabId[] = ['overview', 'emails', 'fitment', 'projection-analysis', 'parameters'];

function isTabId(s: string | null): s is RoleFitPairTabId {
  return s != null && (VALID_TABS as string[]).includes(s);
}

export function RoleFitPairDetailPage() {
  const { candidateId, jobId } = useParams<{ candidateId: string; jobId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [savedProjection, setSavedProjection] = useState<RoleFitProjection | null>(null);
  const [projectionLoading, setProjectionLoading] = useState(false);

  const tabFromUrl = searchParams.get('tab');
  const activeTab: RoleFitPairTabId = isTabId(tabFromUrl) ? tabFromUrl : 'overview';
  const returnToPath = searchParams.get('return');
  const fromRoleFitAi = returnToPath != null && returnToPath.startsWith('/role-fit-ai');
  const backHref = fromRoleFitAi ? returnToPath! : '/role-fit';
  const backLabel = fromRoleFitAi ? 'Back to RoleFit AI' : 'Back';

  const setTab = useCallback(
    (tab: RoleFitPairTabId) => {
      const next = new URLSearchParams();
      if (returnToPath) next.set('return', returnToPath);
      if (tab !== 'overview') next.set('tab', tab);
      setSearchParams(next, { replace: true });
    },
    [setSearchParams, returnToPath],
  );

  useEffect(() => {
    if (!candidateId || !jobId || !user?.orgId) return;
    setLoading(true);
    setNotFound(false);
    Promise.all([candidateService.getById(candidateId), jobService.getById(jobId)])
      .then(([c, j]) => {
        if (!c || !j || c.org_id !== user.orgId || j.org_id !== user.orgId) {
          setNotFound(true);
          setCandidate(null);
          setJob(null);
          return;
        }
        setCandidate(c);
        setJob(j);
      })
      .catch(() => {
        setNotFound(true);
        setCandidate(null);
        setJob(null);
      })
      .finally(() => setLoading(false));
  }, [candidateId, jobId, user?.orgId]);

  useEffect(() => {
    if (!candidateId || !jobId || !user?.orgId) return;
    setProjectionLoading(true);
    rolefitProjectionService
      .getByPair(user.orgId, candidateId, jobId)
      .then(setSavedProjection)
      .catch(() => setSavedProjection(null))
      .finally(() => setProjectionLoading(false));
  }, [candidateId, jobId, user?.orgId]);

  const row = useMemo(() => {
    if (!candidate || !job) return null;
    return computeRoleFitPair(candidate, job);
  }, [candidate, job]);

  const handleCandidateSaved = useCallback((updated: Candidate) => {
    setCandidate(updated);
  }, []);

  if (!user?.orgId) return null;

  if (loading) return <FullPageSpinner />;

  if (notFound || !row || !candidate || !job) {
    return (
      <div className="max-w-6xl">
        <p className="text-gray-400 mb-4">This candidate × job pair was not found or is not in your organization.</p>
        <Link to={backHref} className="btn-secondary inline-flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
      </div>
    );
  }

  const breadcrumbLabel = `${candidate.full_name} — ${job.title}`;

  return (
    <div className="w-full pb-8">
      <PageHeader
        icon={BarChart3}
        breadcrumb={[
          ...(fromRoleFitAi
            ? [
                { label: 'Matching & AI', href: '/single-optimize' },
                { label: 'RoleFit AI', href: returnToPath! },
              ]
            : [
                { label: 'Matching', href: '/role-fit' },
                { label: 'Role fit matrix', href: '/role-fit' },
              ]),
          { label: breadcrumbLabel },
        ]}
        title={candidate.full_name}
        subtitle={[job.title, job.client?.name].filter(Boolean).join(' @ ') || job.title}
        action={
          <Link to={backHref} className="btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> {backLabel}
          </Link>
        }
      />

      <RoleFitPairDetailView
        row={row}
        orgId={user.orgId}
        userId={user.id}
        activeTab={activeTab}
        setTab={setTab}
        onCandidateSaved={handleCandidateSaved}
        optimizeReturnTo={fromRoleFitAi ? returnToPath! : '/role-fit'}
        savedProjection={savedProjection}
        projectionLoading={projectionLoading}
      />
    </div>
  );
}
